import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import DivForm from 'src/components/ModalDialogMUI6/styled/DivForm';
import { PERMISSIONS, STYLE_OPTIONS } from 'src/constants';
import helpers from 'src/helpers';
import { getSmartFieldDefinitionsForPicker } from 'src/actions/smartFieldsActions';
import { SF_ENTITY_LABELS } from 'src/constants/smartFieldLabels';

const filterOptions = createFilterOptions({
  stringify: ({
    label, value
  }) => `${label} ${value}`
});

const isOptionEqualToValue = (option, value) => value?.value && value?.value === option?.value;

const renderOptionAutocomplete = (props, option) => (
  <div
    {...props}
    style={{
      alignItems: 'start',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <b>{option.label}</b>
    <Box
      sx={{
        color: STYLE_OPTIONS.COLOR_BLUE,
        fontFamily: STYLE_OPTIONS.FONT_FAMILY,
        fontSize: '12px',
      }}
    >
      {option.value}
    </Box>
  </div>
);

function ModalInsertDynamicFields({
  isModalVisible,
  dynamicFields,
  onInsert,
  onCloseModal,
}) {
  const dispatch = useDispatch();
  const [selectedField, setSelectedField] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const { user } = useSelector((state) => state.account);
  const { pickerDefinitions, pickerLoading } = useSelector(
    (state) => state?.smartFields || {}
  );

  const userRolePermissions = user?.userRole?.permissions || [];
  const hasSmartFieldsView = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_FIELDS_VIEW,
    userRolePermissions
  );

  // Fetch Contact smart field definitions when modal opens
  useEffect(() => {
    if (isModalVisible && hasSmartFieldsView) {
      dispatch(getSmartFieldDefinitionsForPicker('Contact'));
    }
  }, [isModalVisible, hasSmartFieldsView, dispatch]);

  const cpDynamicFields = useMemo(
    () => dynamicFields
      .filter((field) => (field?.screens?.includes('All') || field?.screens?.includes('Chat')) && field?.softwareName === 'Connection Point')
      .map((field) => ({
        label: field?.description,
        value: field?.value,
      })),
    [dynamicFields]
  );

  // Map Smart Field definitions to autocomplete options
  const smartFieldOptions = useMemo(
    () => (pickerDefinitions || []).map((def) => ({
      label: def.displayLabel || def.fieldLabel,
      value: `[SFC:${def.displayLabel || def.fieldLabel}]`,
    })),
    [pickerDefinitions]
  );

  const handleClose = () => {
    onCloseModal();
    setSelectedField(null);
    setActiveTab(0);
  };

  const handleInsert = () => {
    const selectedValue = selectedField?.value || null;
    if (selectedValue) {
      onInsert(selectedValue);
    }
    handleClose();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedField(null);
  };

  const showSmartFieldsTab = hasSmartFieldsView;

  return (
    <Dialog
      open={isModalVisible}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '24px',
        },
      }}
    >
      <Box
        sx={{
          padding: '0 0 0 16px',
          textAlign: 'center',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            padding: 0,
          }}
        >
          {showSmartFieldsTab
            ? 'Select a Field & Insert'
            : 'Select Dynamic Fields & Insert'}
        </DialogTitle>
      </Box>
      {showSmartFieldsTab && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 1 }}
        >
          <Tab label="Dynamic Fields" />
          <Tab label="Smart Fields" />
        </Tabs>
      )}
      <DivForm>
        {activeTab === 0 && (
          <Autocomplete
            options={cpDynamicFields || []}
            value={selectedField}
            filterOptions={filterOptions}
            renderOption={renderOptionAutocomplete}
            isOptionEqualToValue={isOptionEqualToValue}
            onChange={(e, value) => setSelectedField(value)}
            renderInput={(params) => (
              <TextField
                label="Dynamic Fields"
                placeholder="Search or select a dynamic field"
                {...params}
              />
            )}
            handleHomeEndKeys
            selectOnFocus
            clearOnBlur
          />
        )}
        {activeTab === 1 && (
          <Autocomplete
            options={smartFieldOptions}
            value={selectedField}
            loading={pickerLoading}
            filterOptions={filterOptions}
            renderOption={renderOptionAutocomplete}
            isOptionEqualToValue={isOptionEqualToValue}
            onChange={(e, value) => setSelectedField(value)}
            renderInput={(params) => (
              <TextField
                label={SF_ENTITY_LABELS.Contact}
                placeholder="Search or select a smart field"
                {...params}
              />
            )}
            handleHomeEndKeys
            selectOnFocus
            clearOnBlur
          />
        )}
      </DivForm>
      <DialogActions
        sx={{
          padding: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY_REVERSED,
            minWidth: '105px',
            marginRight: '14px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleInsert}
          disabled={!selectedField?.value}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY,
            minWidth: '105px',
          }}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalInsertDynamicFields.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
  dynamicFields: PropTypes.array,
};
ModalInsertDynamicFields.defaultProps = {
  dynamicFields: [],
};

export default ModalInsertDynamicFields;
